// The verdict card: a v2 game's pick, as librarian's Q3 judge made it (B29 P2.6).
//
// A v2 game's `box_score` carries `verdict`. That holds the run that made it, a summary,
// and per market a decision (a pick at Lean, Play or Strong, or a pass) with its
// rationale, plus the game's best bet. The seven advocate cases that argued each side
// sit beside it in `box_score`. Brian's 2.a folds them in here, under each market,
// without the advocates' own confidence numbers: the judge's scores make the pick, so
// an advocate's "72" beside a pass would read as a contradiction.
//
// **Who sees it:** signed-in visitors (Free and Pro), in every render mode (Brian's 1.a).
// The page passes its own `isSignedIn`, so the card agrees with Projections and Comments.
//
// **What it keys on:** `decision`, never on whether a pick is present. A pass can still
// carry the side it leaned to.

import React from 'react';
import Card from 'react-bootstrap/Card';
import ReactMarkdown from 'react-markdown';
import { styles } from '../../styles/styles.js';
import { isNum } from '../../utils/format.js';
import { isV2, resultColor, resultTitle, unitsText } from '../../utils/picks.js';

const MARKETS = [
  ['moneyline', 'Moneyline', ['moneyline_favored', 'moneyline_underdog']],
  ['spread', 'Spread', ['spread_favored', 'spread_underdog']],
  ['total', 'Total', ['over', 'under']],
  ['draw', 'Draw', ['moneyline_draw']],
];

const RUN_KIND = { initial: 'First read', line_move: 'After a line move', final: 'Final read', manual: 'On request' };

const cardStyle = {
  marginBottom: '10px', backgroundColor: 'black', color: 'white',
  borderColor: 'white', borderWidth: 1, borderStyle: 'solid', padding: 20,
};

const badge = (background, color = 'black') => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontWeight: 'bold',
  fontSize: '0.9em', background, color, marginLeft: 8,
});

const TIER_BADGE = {
  Strong: badge('#FFD700'),
  Play: badge('#4caf50'),
  Lean: badge('#9ecbff'),
};
const PASS_BADGE = badge('transparent', 'rgba(255,255,255,0.6)');

const text = (value) => (typeof value === 'string' && value.trim() ? value : null);

const americanPrice = (price) => {
  if (!isNum(price)) return null;
  return price > 0 ? `+${price}` : `${price}`;
};

const when = (iso) => {
  const date = new Date(iso);
  if (!text(iso) || Number.isNaN(date.getTime())) return null;
  return date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// The favourite's and the underdog's names, from whichever market says which side a team
// is on. Null when no market does, and the cases then fall back to "the favourite".
const sides = (fixture, markets) => {
  const teams = [fixture.home_team, fixture.away_team].map((t) => (typeof t === 'string' ? t : t?.name));
  for (const key of ['spread', 'moneyline']) {
    const m = markets[key];
    if (!m || !text(m.team) || !teams.includes(m.team)) continue;
    const other = teams.find((t) => t && t !== m.team) || null;
    if (m.side === 'favourite') return { fav: m.team, dog: other };
    if (m.side === 'underdog') return { fav: other, dog: m.team };
  }
  return { fav: null, dog: null };
};

const caseTitle = (key, names) => ({
  moneyline_favored: names.fav ? `The case for ${names.fav}` : 'The case for the favourite',
  moneyline_underdog: names.dog ? `The case for ${names.dog}` : 'The case for the underdog',
  spread_favored: names.fav ? `The case for ${names.fav}` : 'The case for the favourite',
  spread_underdog: names.dog ? `The case for ${names.dog}` : 'The case for the underdog',
  over: 'The case for the over',
  under: 'The case for the under',
  moneyline_draw: 'The case for the draw',
}[key]);

// The pick as the card prints it. The total's "Favourite vs Underdog" repeats the page's
// title, so it goes.
const pickText = (m) => {
  const pick = text(m.pick);
  if (!pick) return null;
  const team = text(m.team);
  const trimmed = team && pick.startsWith(`${team} `) && / vs /.test(team) ? pick.slice(team.length + 1) : pick;
  const price = americanPrice(m.price);
  return price ? `${trimmed} (${price})` : trimmed;
};

const Verdict = ({ fixture, signedIn }) => {
  // The slate's placeholder has no `box_score` at all while the detail loads.
  if (!signedIn || !fixture || !('box_score' in fixture)) return null;
  const box = fixture.box_score;
  if (!box || box.pipeline_version !== 2) return null;

  const verdict = box.verdict;
  if (!verdict || typeof verdict !== 'object') {
    return (
      <div className="verdict" style={{ marginTop: '20px' }}>
        <h5 style={{ marginBottom: '15px' }}>Verdict</h5>
        <p style={styles.analysis}>Verdict pending.</p>
      </div>
    );
  }

  const markets = verdict.markets && typeof verdict.markets === 'object' ? verdict.markets : {};
  const closed = verdict.closed && typeof verdict.closed === 'object' ? verdict.closed : {};
  const plays = (Array.isArray(fixture.suggestions) ? fixture.suggestions : []).filter(isV2);
  const names = sides(fixture, markets);
  const run = verdict.run && typeof verdict.run === 'object' ? verdict.run : {};
  const runLine = [RUN_KIND[run.kind] || null, when(run.at)].filter(Boolean).join(' · ');

  return (
    <div className="verdict" style={{ marginTop: '20px' }}>
      <h5 style={{ marginBottom: '10px' }}>Verdict</h5>
      {runLine && <div className="verdict-run" style={{ opacity: 0.7, marginBottom: '10px' }}>{runLine}</div>}
      {text(verdict.summary) && <p className="verdict-summary" style={styles.analysis}>{verdict.summary}</p>}
      {MARKETS.filter(([key]) => markets[key] || closed[key]).map(([key, name, caseKeys]) => {
        const m = markets[key] && typeof markets[key] === 'object' ? markets[key] : null;
        const isPick = m?.decision === 'pick';
        const play = plays.find((p) => p.bet_type === key);
        const result = isPick && play && play.result ? play.result : null;
        const cases = caseKeys.filter((k) => text(box[k]?.text));
        return (
          <Card key={key} className="verdict-market" data-market={key} style={cardStyle}>
            <Card.Body>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                <b>{name}</b>
                {!m && text(closed[key]) && <span style={PASS_BADGE}>Closed</span>}
                {m && !isPick && <span className="verdict-tier" style={PASS_BADGE}>Pass</span>}
                {isPick && (
                  <span className="verdict-tier" style={TIER_BADGE[m.tier] || TIER_BADGE.Lean}>
                    {[m.tier, unitsText(m.units)].filter(Boolean).join(' · ')}
                  </span>
                )}
                {isPick && verdict.best_bet === key && <span className="verdict-best" style={badge('white')}>★ Best bet</span>}
                {result && (
                  <span className="verdict-result" title={resultTitle(result)}
                        style={badge(resultColor(result), 'black')}>
                    {resultTitle(result)}
                  </span>
                )}
              </div>
              {isPick && pickText(m) && <div className="verdict-pick" style={{ marginTop: '8px', fontSize: '1.15em' }}>{pickText(m)}</div>}
              {!m && text(closed[key]) && <div style={{ marginTop: '8px', opacity: 0.7 }}>Closed: {closed[key]}</div>}
              {m && text(m.rationale) && <p style={{ ...styles.analysis, marginTop: '8px' }}>{m.rationale}</p>}
              {m && text(m.nudge_note) && <p style={{ ...styles.analysis, opacity: 0.8 }}>The judge: {m.nudge_note}</p>}
              {cases.length > 0 && (
                <details className="verdict-cases" style={{ marginTop: '8px' }}>
                  <summary style={{ cursor: 'pointer', opacity: 0.85 }}>The case for each side</summary>
                  {cases.map((k) => (
                    <div key={k} style={{ marginTop: '10px' }}>
                      <b>{caseTitle(k, names)}</b>
                      <div style={styles.analysis}><ReactMarkdown>{box[k].text}</ReactMarkdown></div>
                    </div>
                  ))}
                </details>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

export default Verdict;
